/**
 * @function dft Discrete Fourier transform of a real signal x_0 ... x_{N-1}:
 *   c_k = (1/N) * sum_n x_n * e^(-2i*pi*k*n/N)
 * Only k = 0 ... floor(N/2) are computed: for a real signal c_{N-k} is the conjugate of c_k.
 * @param {Array<Number>} points Samples of the signal.
 * @return {Array<{re: Number, im: Number}>} The coefficients c_k.
 */
export function dft(points) {
  const numPoints = points.length;
  //e^(-2i*pi*m/N) only depends on m = k*n mod N, so the cosines and sines are computed once.
  const cosTable = [];
  const sinTable = [];
  for (let m = 0; m < numPoints; m++) {
    cosTable.push(Math.cos((2 * Math.PI * m) / numPoints));
    sinTable.push(Math.sin((2 * Math.PI * m) / numPoints));
  }

  const fourierCoef = [];
  for (let k = 0; k <= Math.floor(numPoints / 2); k++) {
    let re = 0;
    let im = 0;
    for (let n = 0; n < numPoints; n++) {
      const m = (k * n) % numPoints;
      re += points[n] * cosTable[m];
      im -= points[n] * sinTable[m];
    }
    //The 1/N scaling makes the signal the plain sum of its coefficients.
    fourierCoef.push({ re: re / numPoints, im: im / numPoints });
  }
  return fourierCoef;
}

/**
 * @function fourierSeries Real Fourier series of a sampled periodic signal. With t_n = 2*pi*n/N it is exact at the samples:
 *   x(t) = mean + sum_k amp_k * cos(freq_k * t + phase_k)
 * where amp_k = 2|c_k| (|c_k| for the Nyquist frequency N/2) and phase_k = arg(c_k).
 * Each term is one epicycle: a circle of radius amp_k turning freq_k times per period, starting at the angle phase_k.
 * Terms are sorted by decreasing power, so the first K terms are the best K-term approximation (least squares).
 * @param {Array<Number>} points Samples of the signal.
 */
export function fourierSeries(points) {
  const numPoints = points.length;
  const coefs = dft(points);
  const terms = [];
  for (let k = 1; k < coefs.length; k++) {
    const { re, im } = coefs[k];
    const modulus = Math.hypot(re, im);
    //The Nyquist frequency is its own conjugate, every other frequency k is paired with N - k.
    const nyquist = 2 * k === numPoints;
    terms.push({
      freq: k,
      re,
      im,
      amp: nyquist ? modulus : 2 * modulus,
      phase: Math.atan2(im, re),
      //Share of the mean square deviation (1/N) * sum (x_n - mean)^2 carried by the term (Parseval).
      power: nyquist ? modulus * modulus : 2 * modulus * modulus,
    });
  }
  terms.sort((a, b) => b.power - a.power);
  const totalPower = terms.reduce((sum, f) => sum + f.power, 0);
  return { numPoints, mean: coefs.length ? coefs[0].re : 0, terms, totalPower };
}

/**
 * @function evaluate Value at time t of the series truncated to its K largest terms.
 */
export function evaluate(series, K, t) {
  let value = series.mean;
  const count = Math.min(K, series.terms.length);
  for (let j = 0; j < count; j++) {
    const f = series.terms[j];
    value += f.amp * Math.cos(f.freq * t + f.phase);
  }
  return value;
}

/**
 * @function reconstruct Samples the series truncated to its K largest terms at numSamples times evenly spread over [0, 2*pi).
 */
export function reconstruct(series, K, numSamples) {
  const values = [];
  for (let i = 0; i < numSamples; i++) {
    values.push(evaluate(series, K, (2 * Math.PI * i) / numSamples));
  }
  return values;
}

/**
 * @function keptPower Power of the K largest terms. By Parseval, totalPower - keptPower is the mean square error at the samples.
 */
export function keptPower(series, K) {
  let power = 0;
  const count = Math.min(K, series.terms.length);
  for (let j = 0; j < count; j++) {
    power += series.terms[j].power;
  }
  return power;
}

/**
 * @function chain Joints of a chain of epicycles: circle j is centred on the tip of circle j - 1.
 * The rotation turns the whole chain: 0 reads the sum on the x axis, -pi/2 on the (downward) canvas y axis.
 * @return {Array<{x: Number, y: Number, r: Number}>} The K + 1 joints, the last one being the tip; r is the radius of the circle centred on the joint.
 */
export function chain(terms, K, time, x, y, rotation, scale = 1) {
  const joints = [];
  const count = Math.min(K, terms.length);
  for (let j = 0; j < count; j++) {
    const { freq, amp, phase } = terms[j];
    joints.push({ x, y, r: amp * scale });
    x += amp * scale * Math.cos(freq * time + phase + rotation);
    y += amp * scale * Math.sin(freq * time + phase + rotation);
  }
  joints.push({ x, y, r: 0 });
  return joints;
}
